import { loadConsent } from "@/lib/consent/storage";

const STORAGE_KEY = "creategy.utm";
const CLICK_ID_STORAGE_KEY = "creategy.clickids";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const EXTRA_KEYS = ["fbclid"] as const;
/** Ticket TR1: Google-Klick-IDs, erfasst auch ohne UTM-Parameter. */
export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"] as const;
const ALL_KEYS = [...UTM_KEYS, ...EXTRA_KEYS] as const;

type UtmKey = (typeof ALL_KEYS)[number];
type ClickIdKey = (typeof CLICK_ID_KEYS)[number];
export type ClickIds = Partial<Record<ClickIdKey, string>>;

// Zeeg utm shape: fixed keys with string | null values
type ZeegUtm = {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
};

/**
 * Ticket TR1: Klick-IDs ohne Marketing-Zustimmung liegen nur im Speicher der
 * geöffneten Seite (verfallen mit dem Besuch) und werden erst bei Zustimmung
 * abgelegt.
 */
let pendingClickIds: ClickIds = {};

function hasMarketingConsent(): boolean {
  return loadConsent()?.categories.marketing === true;
}

function readJson<T extends object>(key: string): Partial<T> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Partial<T>) : {};
  } catch {
    return {};
  }
}

function writeJson(key: string, data: object): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(data));
  } catch {
    // ignore (SSR / private mode / quota)
  }
}

function readStorage(): Partial<Record<UtmKey, string>> {
  return readJson<Record<UtmKey, string>>(STORAGE_KEY);
}

/**
 * Reads UTM (and fbclid) params from window.location.search and, if any UTM
 * is present, persists the whole set to sessionStorage. Google click IDs are
 * captured independently of UTM (Ticket TR1).
 */
export function captureUtmFromLocation(): void {
  if (typeof window === "undefined") return;
  const search = window.location.search;
  if (!search) return;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return;
  }
  const captured: Partial<Record<UtmKey, string>> = {};
  let hasUtm = false;
  for (const key of ALL_KEYS) {
    const value = params.get(key);
    if (value) {
      captured[key] = value;
      if ((UTM_KEYS as readonly string[]).includes(key)) hasUtm = true;
    }
  }
  if (hasUtm) {
    writeStorage(captured);
  }

  const clickIds: ClickIds = {};
  for (const key of CLICK_ID_KEYS) {
    const value = params.get(key);
    if (value) clickIds[key] = value.slice(0, 200);
  }
  if (Object.keys(clickIds).length > 0) {
    pendingClickIds = { ...pendingClickIds, ...clickIds };
    persistClickIdsIfConsented();
  }
}

function writeStorage(data: Partial<Record<UtmKey, string>>): void {
  writeJson(STORAGE_KEY, data);
}

/** Legt die vorgehaltenen Klick-IDs ab, sobald Marketing-Zustimmung vorliegt. */
export function persistClickIdsIfConsented(): void {
  if (Object.keys(pendingClickIds).length === 0) return;
  if (!hasMarketingConsent()) return;
  writeJson(CLICK_ID_STORAGE_KEY, { ...readJson<ClickIds>(CLICK_ID_STORAGE_KEY), ...pendingClickIds });
  pendingClickIds = {};
}

/** Klick-IDs dieses Besuchs: abgelegte plus noch vorgehaltene. */
export function getClickIds(): ClickIds {
  return { ...readJson<ClickIds>(CLICK_ID_STORAGE_KEY), ...pendingClickIds };
}

/** Nur für Tests. */
export function __resetPendingClickIds(): void {
  pendingClickIds = {};
}

/** Ticket TR1: UTM-Werte und gclid für versteckte Formularfelder. */
export function getTrackingFields(): Partial<Record<UtmKey | "gclid", string>> {
  const out: Partial<Record<UtmKey | "gclid", string>> = { ...readStorage() };
  delete out.fbclid;
  const gclid = getClickIds().gclid;
  if (gclid) out.gclid = gclid;
  return out;
}

/**
 * Returns UTM params in the shape Zeeg expects: fixed keys, null when missing,
 * plus gclid (Ticket TR1).
 */
export function getZeegUtmParams(): ZeegUtm & { gclid: string | null } {
  const stored = readStorage();
  return {
    source: stored.utm_source ?? null,
    medium: stored.utm_medium ?? null,
    campaign: stored.utm_campaign ?? null,
    content: stored.utm_content ?? null,
    term: stored.utm_term ?? null,
    gclid: getClickIds().gclid ?? null,
  };
}
